/** Names of the analytics events emitted by the tab tracking service */
export enum TabTrackingEventName {
  TabOpened = 'tab_opened',
  TabClosed = 'tab_closed',
  TabFocus = 'tab_focus',
  TabBlur = 'tab_blur',
  LinkOpened = 'link_opened',
  ListState = 'list_state',
  FiltersChanged = 'filters_changed',
}

/** How a link or a window was opened */
export enum LinkOpenMode {
  MiddleClick = 'middle_click',
  CtrlClick = 'ctrl_click',
  ContextMenu = 'context_menu',
  WindowOpen = 'window_open',
  TargetBlank = 'target_blank',
  /** "Open link in new tab" from the browser menu of a link: only the opened tab can tell */
  BrowserMenu = 'browser_menu',
}

/** Why the filters of a list changed */
export enum FiltersChangeReason {
  /** The user picked a filter value */
  User = 'user',
  /** The user cleared the filters */
  Reset = 'reset',
  /** The URL changed by itself: back / forward, restored persistent filters, links */
  Url = 'url',
}

/** Where a link points to, as reported by `link_opened` */
export enum LinkTargetKind {
  /** Same host as the app */
  Internal = 'internal',
  /** Another WeMaintain host (client platform, reports) */
  Wemaintain = 'wemaintain',
  Salesforce = 'salesforce',
  Netsuite = 'netsuite',
  Other = 'other',
}

export interface OpenWindowContext {
  /** The component or state opening the window */
  sourceComponent?: string
  /** Overrides the mode, `window_open` by default */
  mode?: LinkOpenMode
}

/**
 * Left in `localStorage` by a tab opening a same-origin link, picked up by the new tab:
 * new tabs opened with `noreferrer` have no referrer and no opener otherwise.
 */
export interface TabHandoff {
  path: string
  mode: LinkOpenMode
  sourceTabId: string | null
  sourceRoute: string
  sourceComponent?: string | null
  /** Epoch ms */
  at: number
}

/** How an event should leave the page */
export enum TabTrackingTransport {
  /** Regular analytics call */
  Default = 'default',
  /** The page is going away: use `navigator.sendBeacon` */
  Beacon = 'beacon',
}

export interface TabTrackingEvent {
  name: TabTrackingEventName
  properties: Record<string, unknown>
  transport: TabTrackingTransport
}

/** What is kept in `sessionStorage['wm-tab-id']` */
export interface StoredTabIdentity {
  id: string
  /** Epoch ms */
  openedAt: number
  seq: number
}

export interface ResolvedTabIdentity extends StoredTabIdentity {
  /** True when this document starts a new tab (no identity, or a copied one) */
  isNew: boolean
  /** True when an identity was found but belongs to another tab (duplicated tab) */
  copiedSession: boolean
}

/** What is kept in `localStorage['wm-tab-seq']`, shared by every tab */
export interface TabSequence {
  sessionId: string | null
  seq: number
  /** Epoch ms of the last increment */
  at: number
}

/** Properties attached to every `page()` and `track()` call */
export interface TabProperties {
  tabId: string
  tabOpenedAt: string
  tabSeq: number
  rumSessionId: string | null
  tabsOpen: number | null
}

export interface RouteEntity {
  entityType: string | null
  entityId: string | null
}
